import { SyntheticEvent, useCallback, useRef } from 'react';
import { useFormikContext, useField } from 'formik';
import { BaseAutocomplete } from '../../../BaseAutocomplete/BaseAutocomplete';
import { WebFormAutocompleteProps } from '../FormAutocomplete.types';
import { FilterOptionsState } from '@mui/material';
import { useScrollToOnError } from '../../../../../helpers/useScrollTo';

export const WebFormAutocomplete = <T,>({
    name,
    id,
    title,
    label,
    ariaLabel,
    labelClass,
    disabled,
    options,
    forceBottomPopper,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
}: WebFormAutocompleteProps<T>) => {
    const [field, meta] = useField(name);
    const { submitCount, setFieldValue } = useFormikContext();

    const inputRef = useRef<HTMLInputElement | null>(null);
    useScrollToOnError({
        scrollable: inputRef,
        name: name,
    });

    const updateFieldValue = useCallback((option: string | T | null) => {
        return setFieldValue(name, (option as string)?.toUpperCase());
    }, [name, setFieldValue]);

    const getOptionLabel = (option: string | T): string => {
        return option as string;
    };

    const renderOption = (option: string | T): string => {
        return option as string;
    };

    const filterOptions = (options: T[], state: FilterOptionsState<T>): T[] => {
        if (state.inputValue === '') {
            return options;
        }
        const filteredOptions = options.filter((option: T) => (option as string)?.toLocaleLowerCase()
            .includes(state.inputValue?.toLocaleLowerCase()));
        if (filteredOptions.length > 0) {
            return filteredOptions;
        }
        else {
            return [];
        }
    };

    const handleOnChange = useCallback((event: SyntheticEvent<Element, Event>, value: string) => {
        updateFieldValue(value);
    }, [updateFieldValue]);

    return (
        <BaseAutocomplete
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            labelClass={labelClass}
            inputRef={inputRef}
            inputValue={field.value ?? ''}
            disabled={disabled}
            touched={meta.touched || submitCount > 0}
            error={meta.error}
            options={options}
            filterOptions={filterOptions}
            onOptionSelected={updateFieldValue}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            onInputChange={handleOnChange}
            forceBottomPopper={forceBottomPopper}
            capsLock={capsLock}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            freeSolo={true}
            openOnFocus={true}
        />
    );
};